import { InventoryItem, DbInventoryItem, InventoryFormData } from '@/types/entities/inventory';
import { Database } from '@/types/supabase';

// Обновлено для унифицированной таблицы inventory
type DbInventoryItemInsert = Database['public']['Tables']['inventory']['Insert'];
type DbInventoryItemUpdate = Database['public']['Tables']['inventory']['Update'];

/**
 * Маппер из БД в frontend (snake_case -> camelCase)
 */
export function mapDbInventoryItemToInventoryItem(dbItem: DbInventoryItem): InventoryItem {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapDbInventoryItemToInventoryItem] Input dbItem:', dbItem);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapDbInventoryItemToInventoryItem] Input quantity from DB:', dbItem.quantity);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapDbInventoryItemToInventoryItem] Input purchase_price from DB:', dbItem.purchase_price);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapDbInventoryItemToInventoryItem] Input rental_point_id from DB:', dbItem.rental_point_id);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapDbInventoryItemToInventoryItem] Input serial_number from DB:', dbItem.serial_number);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapDbInventoryItemToInventoryItem] Input status from DB:', dbItem.status);
  }
  
  const extra = dbItem as unknown as {
    public_id?: string;
    category?: string | null;
    condition?: string | null;
    deposit_amount?: number | null;
    deposit_required?: boolean | null;
  };

  const result = {
    id: dbItem.id,
    userId: dbItem.user_id,
    createdAt: new Date(dbItem.created_at || new Date().toISOString()),
    updatedAt: new Date(dbItem.updated_at || new Date().toISOString()),
    publicId: extra.public_id || undefined,
    name: dbItem.name,
    // Use human-readable category name when available; fallback to category_id
    category: extra.category ?? dbItem.category_id,
    dailyPrice: dbItem.daily_price,
    hourlyPrice: dbItem.hourly_price,
    status: dbItem.status,
    description: dbItem.description,
    sku: dbItem.sku,
    imageUrl: dbItem.image_url ?? null,
    equipment: (dbItem as unknown as { equipment?: string | null }).equipment ?? null,
    
    // Дополнительные поля (используем реальные данные из БД)
    quantity: dbItem.quantity || 1, // ИСПРАВЛЕНО: используем реальное количество из БД
    // locationId удалено - используется только rentalPointId
    rentalPointId: dbItem.rental_point_id || null, // ИСПРАВЛЕНО: используем реальный пункт аренды из БД
    purchasePrice: dbItem.purchase_price || null, // ИСПРАВЛЕНО: используем реальную цену покупки из БД
    purchaseDate: dbItem.purchase_date || null, // ИСПРАВЛЕНО: используем реальную дату покупки из БД
    condition: extra.condition ?? null, // Используем значение из БД (ожидается ID тэга состояния)
    serialNumber: dbItem.serial_number || null, // ИСПРАВЛЕНО: используем реальный серийный номер из БД
    depositAmount: extra.deposit_amount ?? null, // Используем поля из БД
    depositRequired: extra.deposit_required ?? false, // Используем поля из БД
    contractPrice: (dbItem as unknown as { contract_price?: number | null }).contract_price ?? null,
  };
  
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapDbInventoryItemToInventoryItem] Output result:', result);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapDbInventoryItemToInventoryItem] Output quantity:', result.quantity);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapDbInventoryItemToInventoryItem] Output purchasePrice:', result.purchasePrice);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapDbInventoryItemToInventoryItem] Output rentalPointId:', result.rentalPointId);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapDbInventoryItemToInventoryItem] Output serialNumber:', result.serialNumber);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapDbInventoryItemToInventoryItem] Output status:', result.status);
  }
  
  return result;
}

/**
 * Маппер из данных формы (frontend) в payload вставки БД (camelCase -> snake_case)
 * Используется для создания записей из форм, где значения приходят как InventoryFormData.
 */
export function mapInventoryFormDataToDbInsert(
  form: InventoryFormData & { userId: string }
): DbInventoryItemInsert {
  const result: DbInventoryItemInsert = {
    user_id: form.userId,
    name: form.name,
    category_id: form.category ?? null,
    daily_price: form.dailyPrice,
    hourly_price: form.hourlyPrice ?? null,
    status: form.status,
    description: form.description ?? null,
    sku: form.sku,
    image_url: form.imageUrl ?? null,
    quantity: (form.quantity ?? 1) as number,
    purchase_price: form.purchasePrice ?? null,
    rental_point_id: form.rentalPointId ?? null,
    serial_number: form.serialNumber ?? null,
    purchase_date: form.purchaseDate ?? null,
    // Поля состояния/залога
    condition: form.condition ?? null,
    deposit_amount: form.depositAmount ?? null,
    deposit_required: Boolean(form.depositRequired ?? false),
  };

  // Assign optional field not present in generated types
  (result as unknown as Record<string, unknown>).equipment = form.equipment ?? null;
  // Assign contract_price via cast if it's not present in generated Insert type
  (result as unknown as { contract_price?: number | null }).contract_price =
    (form as unknown as { contractPrice?: number | null }).contractPrice ?? null;

  return result;
}

/**
 * Маппер из frontend в БД для создания (camelCase -> snake_case)
 */
export function mapInventoryItemToDbInsert(item: Partial<InventoryItem>): DbInventoryItemInsert {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapInventoryItemToDbInsert] Input item:', item);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapInventoryItemToDbInsert] Input quantity:', item.quantity);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapInventoryItemToDbInsert] Input purchasePrice:', item.purchasePrice);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapInventoryItemToDbInsert] Input rentalPointId:', item.rentalPointId);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapInventoryItemToDbInsert] Input serialNumber:', item.serialNumber);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapInventoryItemToDbInsert] Input status:', item.status);
  }
  
  const extra = item as unknown as {
    condition?: unknown;
    depositAmount?: number | null;
    depositRequired?: boolean | null;
  };

  const result = {
    user_id: item.userId!,
    name: item.name!,
    category_id: item.category || null,
    daily_price: item.dailyPrice ?? 0, // ИСПРАВЛЕНО: передаём 0 вместо null, т.к. поле NOT NULL в БД
    hourly_price: item.hourlyPrice || null,
    status: item.status || 'available',
    description: item.description || null,
    sku: item.sku || null, // SKU необязательное поле
    image_url: item.imageUrl || null,
    quantity: item.quantity || 1, // ИСПРАВЛЕНО: добавлено маппинг количества
    purchase_price: item.purchasePrice || null, // ИСПРАВЛЕНО: добавлено маппинг цены покупки
    rental_point_id: item.rentalPointId || null, // ИСПРАВЛЕНО: добавлено маппинг пункта аренды
    serial_number: item.serialNumber || null, // ИСПРАВЛЕНО: добавлено маппинг серийного номера
    purchase_date: item.purchaseDate || null, // ИСПРАВЛЕНО: добавлено маппинг даты покупки
    // ВАЖНО: не пишем в БД UUID-подобные значения для condition (иначе падает CHECK constraint)
    // Если пришёл ID тега (UUID) — сохраняем NULL, чтобы не нарушать ограничение.
    condition: (() => {
      const raw = extra.condition as unknown;
      if (typeof raw !== 'string') return null;
      const uuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(raw);
      return uuidLike ? null : raw;
    })(),
    deposit_amount: extra.depositAmount ?? null,
    deposit_required: extra.depositRequired ?? false,
    contract_price: (item as unknown as { contractPrice?: number | null }).contractPrice ?? null,
  };

  // Assign optional field not present in generated types
  (result as unknown as Record<string, unknown>).equipment = (item as unknown as { equipment?: string | null }).equipment ?? null;
  
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapInventoryItemToDbInsert] Output result:', result);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapInventoryItemToDbInsert] Output quantity:', result.quantity);
  }
  
  return result;
}

/**
 * Маппер из frontend в БД для обновления (camelCase -> snake_case)
 */
export function mapInventoryItemUpdateToDbUpdate(item: Partial<InventoryItem>): DbInventoryItemUpdate {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapInventoryItemUpdateToDbUpdate] Input item:', item);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapInventoryItemUpdateToDbUpdate] Input rentalPointId:', item.rentalPointId);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapInventoryItemUpdateToDbUpdate] Input purchasePrice:', item.purchasePrice);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapInventoryItemUpdateToDbUpdate] Input serialNumber:', item.serialNumber);
  }
 
  // ВАЖНО: Формируем объект обновления динамически — только переданные поля.
  // Это предотвращает случайную установку NULL в NOT NULL колонках.
  const update: (Partial<DbInventoryItemUpdate> & { updated_at: string } & {
    condition?: string | null;
    deposit_amount?: number | null;
    deposit_required?: boolean;
    contract_price?: number | null;
  }) = {
    updated_at: new Date().toISOString(),
  };

  // Базовые поля
  if (item.name !== undefined) update.name = item.name;
  if (item.category !== undefined) update.category_id = item.category || null;
  if (item.dailyPrice !== undefined) update.daily_price = item.dailyPrice!;
  if (item.hourlyPrice !== undefined) update.hourly_price = item.hourlyPrice ?? null;
  if (item.status !== undefined) update.status = item.status;
  if (item.description !== undefined) update.description = item.description ?? null;
  if ((item as unknown as { equipment?: string | null }).equipment !== undefined) {
    (update as unknown as Record<string, unknown>).equipment = (item as unknown as { equipment?: string | null }).equipment ?? null;
  }
  if (item.sku !== undefined) update.sku = item.sku;
  if (item.imageUrl !== undefined) update.image_url = item.imageUrl ?? null;
  if (item.quantity !== undefined) update.quantity = item.quantity as number; // сохраняем фактическое значение
  if (item.purchasePrice !== undefined) update.purchase_price = item.purchasePrice ?? null;
  if (item.rentalPointId !== undefined) update.rental_point_id = item.rentalPointId ?? null;
  if (item.serialNumber !== undefined) update.serial_number = item.serialNumber ?? null;
  if (item.purchaseDate !== undefined) update.purchase_date = item.purchaseDate ?? null;
  if ((item as unknown as { contractPrice?: number | null }).contractPrice !== undefined) {
    update.contract_price = (item as unknown as { contractPrice?: number | null }).contractPrice ?? null;
  }

  // Доп. поля (состояние/залог)
  const condition = (item as unknown as { condition?: unknown }).condition as unknown;
  if (condition !== undefined) {
    if (typeof condition === 'string') {
      const uuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(condition);
      // Если значение UUID-подобное — пропускаем установку поля, чтобы не нарушить CHECK constraint
      if (!uuidLike) {
        update.condition = condition;
      }
    } else if (condition === null) {
      // Разрешаем явную очистку, если колонка допускает NULL
      update.condition = null;
    }
  }

  const depositAmount = (item as unknown as { depositAmount?: number | null }).depositAmount;
  if (depositAmount !== undefined) update.deposit_amount = depositAmount ?? null;

  const depositRequired = (item as unknown as { depositRequired?: boolean | null }).depositRequired;
  if (depositRequired !== undefined) update.deposit_required = Boolean(depositRequired ?? false);

  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapInventoryItemUpdateToDbUpdate] Output result:', update);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapInventoryItemUpdateToDbUpdate] Output rental_point_id:', update.rental_point_id);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapInventoryItemUpdateToDbUpdate] Output purchase_price:', update.purchase_price);
    // eslint-disable-next-line no-console
    console.debug('[DEBUG mapInventoryItemUpdateToDbUpdate] Output serial_number:', update.serial_number);
  }
  
  return update as DbInventoryItemUpdate;
}
